import { useFormik } from "formik"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"

import axios from "axios"
import { ProjectFormPageProps } from "components/ProjectForm"
import { EProjectFormPages } from "components/ProjectForm/config"
import {
    IPaymentDetailsForm,
    setPaymentDetailsFormValue,
    setProjectId,
    setSurveyPendingElectricityBillFiles,
    setSurveyPendingSpotFiles,
} from "components/ProjectForm/ProjectFormSlice"
import EModuleIds, { ESubModuleIds } from "enums/ModuleEnum"
import {
    DocHubUrlInfo,
    SubmitFileInfo,
    useSubmitFinalUploadsMutation,
    useUploadDocumentsMutation,
} from "pages/Common/fileUploadService"
import { useAddPaymentTermsInBulkMutation, useGetPaymentConditionsQuery } from "pages/Finance/FinanceServices"
import { useRaiseLogisticsTicketMutation } from "pages/Logistics/LogisticsServices"
import { useRaiseSurveyTicketMutation } from "pages/Operations/features/Survey/SurveyService"
import {
    IPaymentTermPayload,
    useLazyGetProjectFormDetailsByStepQuery,
    useUpdateTicketPageDataMutation,
} from "pages/Projects/ProjectServices"
import { TStore } from "store/Store"
import { formatGMTDateToYYYYMMDD } from "utils/utils"
import { PAYMENT_TYPE } from "./PaymentTerms"

const DEFAULT_ALLOCATION_TYPE = { id: "percent", title: "%" }

export enum EPaymentDetailsFields {
    TOTAL_AMOUNT = "total_amount",
    SPECIFY_PAYMENT_TERMS = "specify_payment_terms",
    PAYMENT_TERMS = "payment_terms",
    PAYMENT_TERMS_OBJ = "payment_terms_obj",
}

export type PaymentDetailsFormValues = {
    [EPaymentDetailsFields.TOTAL_AMOUNT]: string
    [EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]: { id: number; title: string } | null
    [EPaymentDetailsFields.PAYMENT_TERMS]: boolean
    [EPaymentDetailsFields.PAYMENT_TERMS_OBJ]: any[]
}

const initialValues: PaymentDetailsFormValues = {
    [EPaymentDetailsFields.TOTAL_AMOUNT]: "",
    [EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]: { id: 1, title: "Yes" },
    [EPaymentDetailsFields.PAYMENT_TERMS]: false,
    [EPaymentDetailsFields.PAYMENT_TERMS_OBJ]: [
        {
            pmt_type: { id: "", title: "" },
            allocation_type: DEFAULT_ALLOCATION_TYPE,
            pmt_percent: "",
            pmt_amount: "",
            time_qty: "",
            time_uom: { id: 1, title: "Days" },
            condition: { id: "", title: "" },
            condition_text: "",
            bank_guarantee: "",
            pmt_due_date: "",
            payment_term_id: "",
            utr: "",
            original_amt: "",
            txn_due_date: "",
            paid_at: "",
            net_deduction: "",
            remarks: "",
            evidence_file_id: "",
        },
    ],
}

export const PAYMENT_TERMS_INITIAL_VALUES = {
    pmt_type: { id: "", title: "" },
    allocation_type: DEFAULT_ALLOCATION_TYPE,
    pmt_percent: "",
    pmt_amount: "",
    time_qty: "",
    time_uom: { id: 1, title: "Days" },
    condition: { id: "", title: "" },
    condition_text: "",
    bank_guarantee: "",
    pmt_due_date: "",
    payment_term_id: "",
    utr: "",
    original_amt: "",
    txn_due_date: "",
    paid_at: "",
    net_deduction: "",
    remarks: "",
    evidence_file_id: "",
}

const isAdvanceCondition = (term: any) => Number(term?.condition?.id) === 7

const hasAdvanceConditionRequiredFields = (term: any) => {
    if (!isAdvanceCondition(term)) {
        return true
    }

    return Boolean(term?.utr && term?.original_amt && term?.txn_due_date && term?.paid_at && term?.net_deduction)
}

const isCustomDatePaymentType = (term: any) => term?.pmt_type?.id === 0

const hasCustomDateRequiredFields = (term: any) => {
    if (!isCustomDatePaymentType(term)) {
        return true
    }

    return Boolean(term?.pmt_due_date)
}

const isAmountAllocation = (term: any) => term?.allocation_type?.id === "amount"

const hasPaymentAllocationValue = (term: any) => {
    return Boolean(isAmountAllocation(term) ? term?.pmt_amount : term?.pmt_percent)
}

const getNumericValue = (value: any) => {
    const parsedValue = parseFloat(String(value ?? 0))

    return Number.isFinite(parsedValue) ? parsedValue : 0
}

const shouldValidatePercentTotal = (paymentTermsObj: any[]) => {
    return paymentTermsObj.length > 0 && paymentTermsObj.every((term: any) => !isAmountAllocation(term))
}

const isPaymentTermsTotalValid = (paymentTermsObj: any[], totalAmountValue: any) => {
    if (paymentTermsObj.length === 0) {
        return true
    }

    const totalAmount = getNumericValue(totalAmountValue)

    if (shouldValidatePercentTotal(paymentTermsObj)) {
        const totalPercent = paymentTermsObj.reduce(
            (acc: number, obj: any) => acc + getNumericValue(obj?.pmt_percent),
            0
        )

        return totalPercent === 100
    }

    if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
        return false
    }

    const fixedAmountTotal = paymentTermsObj.reduce(
        (acc: number, obj: any) => acc + (isAmountAllocation(obj) ? getNumericValue(obj?.pmt_amount) : 0),
        0
    )
    const percentTotal = paymentTermsObj.reduce(
        (acc: number, obj: any) => acc + (!isAmountAllocation(obj) ? getNumericValue(obj?.pmt_percent) : 0),
        0
    )
    const percentAmountTotal = (totalAmount * percentTotal) / 100
    const allocatedTotal = fixedAmountTotal + percentAmountTotal

    return Math.abs(allocatedTotal - totalAmount) < 0.01
}

const hasPaymentTerms = (values: any) => values?.[EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]?.title === "Yes"

const paymentDetailsSchema = Yup.object({
    [EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]: Yup.object({
        id: Yup.number().required(),
        title: Yup.string().required(),
    })
        .nullable()
        .required("Please specify payment terms"),
    [EPaymentDetailsFields.TOTAL_AMOUNT]: Yup.string().when(
        EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS,
        ([specifyPaymentTerms], schema) =>
            specifyPaymentTerms?.title === "Yes" ? schema.required("Amount is required") : schema.notRequired()
    ),
    [EPaymentDetailsFields.PAYMENT_TERMS]: Yup.boolean(),
    [EPaymentDetailsFields.PAYMENT_TERMS_OBJ]: Yup.array().notRequired(),
})
    .test("payment-terms-100-percent", "Payment terms must sum to 100%", function (values): boolean {
        if (!values || !hasPaymentTerms(values)) {
            return true
        }

        const paymentTermsObj = values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ] || []

        return isPaymentTermsTotalValid(paymentTermsObj, values[EPaymentDetailsFields.TOTAL_AMOUNT])
    })
    .test("payment-terms-allocation-required", "Payment term allocation is required", function (values): boolean {
        if (!values || !hasPaymentTerms(values)) {
            return true
        }

        const paymentTermsObj = values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ] || []

        return paymentTermsObj.every(hasPaymentAllocationValue)
    })
    .test("payment-terms-advance-required-fields", "Advance payment fields are required", function (values): boolean {
        if (!values || !hasPaymentTerms(values)) {
            return true
        }

        const paymentTermsObj = values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ] || []

        return paymentTermsObj.every(hasAdvanceConditionRequiredFields)
    })
    .test("payment-terms-custom-date-required", "Payment date is required", function (values): boolean {
        if (!values || !hasPaymentTerms(values)) {
            return true
        }

        const paymentTermsObj = values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ] || []

        return paymentTermsObj.every(hasCustomDateRequiredFields)
    })

const usePaymentDetails = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const hasPrefilled = useRef(false)
    const {
        project_id,
        payment_details_form,
        chargers_and_materials_form,
        logistics_basic_details_form,
        deal_details_form,
        account_n_sites_form,
        survey_basic_details_form,
        survey_pending_spot_files,
        survey_pending_electricity_bill_files,
    } = useSelector((store: TStore) => store.ProjectForm)
    const { user_id } = useSelector((state: any) => state.verifyLogin)

    const [addPaymentTerms, { isLoading: isAddingPaymentTerms }] = useAddPaymentTermsInBulkMutation()
    const [updateTicketPageData, { isLoading: isUpdatingPageData }] = useUpdateTicketPageDataMutation()
    const [getProjectFormDetailsByStep] = useLazyGetProjectFormDetailsByStepQuery()
    const { data: paymentConditions } = useGetPaymentConditionsQuery({
        ticket_id: "",
    })
    const [raiseLogisticsTicket] = useRaiseLogisticsTicketMutation()
    const [raiseSurveyTicket] = useRaiseSurveyTicketMutation()
    const [submitFinalUploads] = useSubmitFinalUploadsMutation()
    const [uploadDocuments] = useUploadDocumentsMutation()

    const uploadFilesToS3 = async (files: File[], fileType: string, ticketId: number): Promise<SubmitFileInfo[]> => {
        if (!files.length) return []
        const response = await uploadDocuments({
            ticket_id: ticketId,
            user_id,
            files: [{ file_type: fileType, file_name: files.map(f => f.name) }],
        }).unwrap()
        const urls: DocHubUrlInfo[] = response.urls[0]?.urls || []
        if (!urls.length) return []
        await Promise.all(
            urls.map((urlInfo: DocHubUrlInfo, i: number) =>
                axios.put(urlInfo.presigned_url, files[i], { headers: { "Content-Type": files[i]?.type } })
            )
        )
        return urls.map((urlInfo: DocHubUrlInfo) => ({
            file_url: urlInfo.file_url,
            doc_id: urlInfo.doc_id,
            report_id: urlInfo.report_id,
        }))
    }

    const conditionOptions = paymentConditions?.map(item => ({ id: item.id, title: item.name })) || []

    const formik = useFormik<PaymentDetailsFormValues>({
        initialValues: payment_details_form.total_amount
            ? {
                  ...initialValues,
                  [EPaymentDetailsFields.TOTAL_AMOUNT]: payment_details_form.total_amount,
                  [EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]: payment_details_form.specify_payment_terms,
                  [EPaymentDetailsFields.PAYMENT_TERMS]: payment_details_form.payment_terms,
                  [EPaymentDetailsFields.PAYMENT_TERMS_OBJ]: payment_details_form.payment_terms_obj?.length
                      ? payment_details_form.payment_terms_obj
                      : initialValues[EPaymentDetailsFields.PAYMENT_TERMS_OBJ],
              }
            : initialValues,
        validationSchema: paymentDetailsSchema,
        validateOnChange: false,
        onSubmit: handleSubmit,
    })

    const fetchAndPrefillPaymentDetailsData = useCallback(async () => {
        if (!props.isEditMode || !props.projectId || hasPrefilled.current) return
        if (props.moduleId === EModuleIds.LOGISTICS) return

        try {
            const response = await getProjectFormDetailsByStep({
                project_id: props.projectId,
                page_title: EProjectFormPages.PAYMENT_DETAILS,
            }).unwrap()

            const pageFormData = response?.pages?.[EProjectFormPages.PAYMENT_DETAILS]
            if (!pageFormData) return

            hasPrefilled.current = true
            const data = pageFormData.page_data || pageFormData

            if (data.total_amount != null)
                formik.setFieldValue(EPaymentDetailsFields.TOTAL_AMOUNT, String(data.total_amount))
            if (data.payment_terms) {
                const hasTerms = Boolean(data.payment_terms)
                formik.setFieldValue(
                    EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS,
                    hasTerms ? { id: 1, title: "Yes" } : { id: 0, title: "No" }
                )
                formik.setFieldValue(EPaymentDetailsFields.PAYMENT_TERMS, hasTerms)
            }
            if (data.payment_terms_obj?.length > 0) {
                const termsObj = data.payment_terms_obj.map((term: any) => {
                    const transaction = term.transactions?.[0] || term.payment_evidence

                    return {
                        pmt_type:
                            term.pmt_type != null
                                ? {
                                      id: term.pmt_type,
                                      title:
                                          term.pmt_type === 0
                                              ? PAYMENT_TYPE.SET_CUSTOM_DATE
                                              : PAYMENT_TYPE.SET_CONDITIONAL_TIME_FRAME,
                                  }
                                : { id: "", title: "" },
                        allocation_type:
                            term.pmt_amount != null ? { id: "amount", title: "Amount" } : DEFAULT_ALLOCATION_TYPE,
                        pmt_percent: term.pmt_percent ? String(term.pmt_percent) : "",
                        pmt_amount: term.pmt_amount != null ? String(term.pmt_amount) : "",
                        time_qty: term.time_qty ? String(term.time_qty) : "",
                        time_uom: term.time_uom
                            ? { id: term.time_uom, title: term.time_uom === 1 ? "Days" : "Months" }
                            : { id: 1, title: "Days" },
                        condition:
                            term.condition != null
                                ? conditionOptions?.find(i => i.id === term.condition) || { id: "", title: "" }
                                : { id: "", title: "" },
                        condition_text: term.condition_text || "",
                        bank_guarantee: term.bank_guarantee ? "1" : "",
                        pmt_due_date: term.pmt_due_date ? formatGMTDateToYYYYMMDD(term.pmt_due_date) : "",
                        payment_term_id: term.payment_term_id || "",
                        utr: transaction?.utr || term.utr || "",
                        original_amt:
                            transaction?.original_amt != null
                                ? String(transaction.original_amt)
                                : term.original_amt != null
                                  ? String(term.original_amt)
                                  : "",
                        txn_due_date: transaction?.txn_due_date
                            ? formatGMTDateToYYYYMMDD(transaction.txn_due_date)
                            : "",
                        paid_at: transaction?.paid_at ? formatGMTDateToYYYYMMDD(transaction.paid_at) : "",
                        net_deduction:
                            transaction?.net_deduction != null
                                ? String(transaction.net_deduction)
                                : term.net_deduction != null
                                  ? String(term.net_deduction)
                                  : "",
                        remarks: transaction?.remarks || term.remarks || "",
                        transaction_id: transaction?.id || term.transaction_id || "",
                        evidence_file_id: transaction?.file_url?.doc_id || transaction?.evidence_file_id || "",
                    }
                })
                formik.setFieldValue(EPaymentDetailsFields.PAYMENT_TERMS_OBJ, termsObj)
            }
        } catch (error) {
            console.error("Error fetching payment details prefill data:", error)
        }
    }, [props.isEditMode, props.projectId, getProjectFormDetailsByStep, formik, conditionOptions])

    useEffect(() => {
        fetchAndPrefillPaymentDetailsData()
    }, [fetchAndPrefillPaymentDetailsData])

    const saveFormValuesToRedux = (value: any, key: keyof IPaymentDetailsForm) => {
        dispatch(setPaymentDetailsFormValue({ value, key }))
    }

    const getPaymentTermsValue = (id: string, key: string) => {
        const index = formik.values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ].findIndex((item: any) => item?.id === id)
        if (index > -1) {
            return (formik.values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ][index] as any)[key]
        }
    }

    function buildSurveyTicketPayload() {
        const s = survey_basic_details_form!
        return {
            template_id: deal_details_form.template_id?.id as number,
            destination_site_id: account_n_sites_form.destination_site_id || undefined,
            accounts: account_n_sites_form.account_roles.map(role => ({
                account_id: role.account_id,
                account_type_id: role.account_type_id,
                billing_address_id: role.billing_address_id,
                contact_id: role.contact_id,
                contact_type_id: role.contact_type_id,
            })),
            ticket_name: s.survey_name,
            category_id: s.category_id?.id as number | undefined,
            business_poc_id: s.business_poc?.id as number | undefined,
            chargers: s.charger_details.map(charger => ({
                charger_type: String(charger.charger_type?.id),
                quantity: parseInt(charger.quantity) || 0,
                power_rating: charger.power_rating ? `${charger.power_rating}` : undefined,
            })),
            available_load: s.available_load || undefined,
            connection_type: s.connection_type?.id as string | undefined,
            connection_owned_by: s.connection_owned_by?.id as string | undefined,
            no_of_spots: s.no_of_spots ? parseInt(s.no_of_spots) : undefined,
            foundation_type: s.foundation_type?.id as string | undefined,
            remarks: s.remarks || undefined,
            visit_by_date: s.visit_by_date,
            time_slot: s.time_slot?.id as number,
        }
    }

    async function handleSubmit(values: PaymentDetailsFormValues) {
        let ticketId
        if (values[EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]?.title === "Yes") {
            const paymentTermsArr = values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ] || []
            if (!paymentTermsArr.every(hasPaymentAllocationValue)) {
                return
            }

            if (!isPaymentTermsTotalValid(paymentTermsArr, values[EPaymentDetailsFields.TOTAL_AMOUNT])) {
                return
            }

            if (!paymentTermsArr.every(hasAdvanceConditionRequiredFields)) {
                return
            }

            if (!paymentTermsArr.every(hasCustomDateRequiredFields)) {
                return
            }
        }

        try {
            const paymentTermsObj: IPaymentTermPayload[] = values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ].map(
                (term: any) => {
                    const isCustomDate = term?.pmt_type?.id === 0
                    const isConditionalTimeFrame = term?.pmt_type?.id === 1
                    const usesAmount = isAmountAllocation(term)

                    let payload: IPaymentTermPayload = {
                        payment_term_id: term?.payment_term_id || null,
                        pmt_type: term?.pmt_type?.id ?? 0,
                        bank_guarantee: term?.bank_guarantee ? 1 : 0,
                        condition: term?.condition?.id ?? 0,
                        condition_text: conditionOptions.find(option => option.id === term?.condition?.id)?.title || "",
                    }

                    if (usesAmount) {
                        payload.pmt_amount = Number(term?.pmt_amount) || 0
                    } else {
                        payload.pmt_percent = Number(term?.pmt_percent) || 0
                    }

                    if (isCustomDate) {
                        payload.pmt_due_date = term?.pmt_due_date || ""
                    }

                    if (isConditionalTimeFrame) {
                        payload.time_qty = Number(term?.time_qty) || 0
                        payload.time_uom = term?.time_uom?.id ?? 1
                    }

                    if (term?.condition?.id === 7) {
                        const paymentEvidence: any = {
                            utr: term?.utr || "",
                            original_amt: term?.original_amt || "",
                            txn_due_date: term?.txn_due_date || "",
                            paid_at: term?.paid_at || "",
                            net_deduction: term?.net_deduction || "",
                            remarks: term?.remarks || "",
                        }

                        if (term?.transaction_id) {
                            paymentEvidence.id = term?.transaction_id
                        }

                        return {
                            ...payload,
                            payment_evidence: paymentEvidence,
                        }
                    }

                    return payload
                }
            )

            const pageTitle =
                (props.pageData?.title as EProjectFormPages.PAYMENT_DETAILS) || EProjectFormPages.PAYMENT_DETAILS
            const paymentTermsValue = values[EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]?.title === "Yes" ? 1 : 0
            const paymentTermsObjValue =
                values[EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]?.title === "Yes"
                    ? paymentTermsObj
                    : ([] as IPaymentTermPayload[])

            if (props.subModuleId === ESubModuleIds.DEMOLITION && !props.isEditMode) {
                saveFormValuesToRedux(
                    paymentTermsValue === 1 ? values[EPaymentDetailsFields.TOTAL_AMOUNT] : "",
                    "total_amount"
                )
                saveFormValuesToRedux(values[EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS], "specify_payment_terms")
                saveFormValuesToRedux(
                    paymentTermsValue === 1 ? values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ] : [],
                    "payment_terms_obj"
                )
                saveFormValuesToRedux(paymentTermsValue === 1, "payment_terms")
                props.handleNextStep()
                return
            }

            if (props.subModuleId === ESubModuleIds.SURVEY && !props.isEditMode && survey_basic_details_form !== null) {
                const surveyResult = await raiseSurveyTicket(buildSurveyTicketPayload()).unwrap()
                ticketId = surveyResult.project_id
                dispatch(setProjectId(surveyResult.project_id))

                const [spotInfos, elecInfos] = await Promise.all([
                    uploadFilesToS3(survey_pending_spot_files, "survey_spot_image", surveyResult.ticket_id),
                    uploadFilesToS3(survey_pending_electricity_bill_files, "survey_others", surveyResult.ticket_id),
                ])
                const allSurveyFileInfos = [...spotInfos, ...elecInfos]
                if (allSurveyFileInfos.length > 0) {
                    await submitFinalUploads({
                        ticket_id: surveyResult.ticket_id,
                        file_infos: allSurveyFileInfos,
                    }).unwrap()
                }
                dispatch(setSurveyPendingSpotFiles([]))
                dispatch(setSurveyPendingElectricityBillFiles([]))
                props.handleNextStep(ticketId)
                return

                // await updateTicketPageData({
                //     page_title: pageTitle,
                //     project_id: surveyResult.project_id,
                //     finance_ticket_id: null,
                //     total_amount: Number(values[EPaymentDetailsFields.TOTAL_AMOUNT]) || 0,
                //     payment_terms: paymentTermsValue,
                //     payment_terms_obj: paymentTermsObjValue,
                // }).unwrap()
            } else {
                let resolvedProjectId: number | null = project_id || null
                let resolvedFinanceId: number | null = null

                if (props.moduleId === EModuleIds.LOGISTICS) {
                    const formValues = {
                        ...logistics_basic_details_form,
                        ...deal_details_form,
                        ...account_n_sites_form,
                        ...payment_details_form,
                    }
                    const logisticsPayload = {
                        template_id: formValues.template_id?.id,
                        project_name: formValues.project_name,
                        source_site_id: formValues.source_site_id,
                        destination_site_id: formValues.destination_site_id,
                        accounts: formValues.account_roles,
                        purchase_order: formValues.purchase_order?.id,
                        category_id: formValues.category_id?.id,
                        visit_by_date: formValues.visit_by_date,
                        visit_by_time: formValues.visit_by_time?.id,
                        total_amount: formik.values[EPaymentDetailsFields.TOTAL_AMOUNT],
                        contains_rfid: false,
                        // Mirrors buildLogisticsPayload in useChargersAndMaterials — the ticket can
                        // be raised from either last page, so both payloads carry the complaint.
                        complaint_id: formValues.complaint_id?.id ?? null,
                        complaint_link_source: formValues.complaint_link_source ?? null,
                        source_poc: {
                            name: formValues.source_poc?.on_ground_poc_name,
                            phone: formValues.source_poc?.on_ground_poc_phone,
                            email: formValues.source_poc?.on_ground_poc_email,
                        },
                        destination_poc: {
                            name: formValues.destination_poc?.on_ground_poc_name,
                            phone: formValues.destination_poc?.on_ground_poc_phone,
                            email: formValues.destination_poc?.on_ground_poc_email,
                        },
                        chargers:
                            chargers_and_materials_form?.chargers?.map(charger => ({
                                product_id: charger.product_id,
                                branding_id: charger.branding,
                                operational_power_rating: charger.power_rating,
                                remarks: charger.display_name,
                            })) || [],
                        materials:
                            chargers_and_materials_form?.materials?.map(material => ({
                                product_id: material.product_id,
                                quantity: material.quantity,
                                remarks: material.description,
                            })) || [],
                    }
                    const logResult = await raiseLogisticsTicket(logisticsPayload).unwrap()
                    resolvedProjectId = logResult.project_id
                    ticketId = logResult.project_id
                    resolvedFinanceId = logResult.finance_id
                    dispatch(setProjectId(logResult.project_id))
                }

                const pageDataPayload = {
                    page_title: pageTitle,
                    project_id: resolvedProjectId,
                    finance_ticket_id: resolvedFinanceId,
                    total_amount: paymentTermsValue === 1 ? Number(values[EPaymentDetailsFields.TOTAL_AMOUNT]) || 0 : 0,
                    payment_terms: paymentTermsValue,
                    payment_terms_obj: paymentTermsObjValue,
                }
                await updateTicketPageData(pageDataPayload).unwrap()
            }

            saveFormValuesToRedux(values[EPaymentDetailsFields.TOTAL_AMOUNT], "total_amount")
            saveFormValuesToRedux(values[EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS], "specify_payment_terms")
            saveFormValuesToRedux(values[EPaymentDetailsFields.PAYMENT_TERMS_OBJ], "payment_terms_obj")

            enqueueSnackbar("Payment details saved successfully", { variant: "success" })
            props.handleNextStep(ticketId)
        } catch (error) {
            console.error("Error saving payment details:", error)
            enqueueSnackbar("Error saving payment details", { variant: "error" })
        }
    }

    function hasError(field: EPaymentDetailsFields) {
        return Boolean(formik.submitCount > 0 && formik.errors[field])
    }

    const showPaymentTerms = formik.values[EPaymentDetailsFields.SPECIFY_PAYMENT_TERMS]?.title === "Yes"

    return {
        states: {
            formik,
            showPaymentTerms,
            isAddingPaymentTerms,
            isUpdatingPageData,
            PAYMENT_TERMS_INITIAL_VALUES,
        },
        functions: {
            handleSubmit,
            hasError,
            saveFormValuesToRedux,
            getPaymentTermsValue,
        },
    }
}

export default usePaymentDetails
